// Gemini.jsx
import React, { useState, useRef, useContext, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import remarkGfm from 'remark-gfm';
import { FaPaperPlane, FaUser, FaRobot } from 'react-icons/fa';
import Feedback from './Feedback';
import { AppContext } from '../../AppContext';
import TypingDots from '../utils/TypingDots';

function Gemini({ feedback, setFeedback }) {
    const { basePath, projectName } = useContext(AppContext).contextValue;

    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [content, setContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef(null);
    const esRef = useRef(null);

    // Typewriter/streaming buffers
    const twQueueRef = useRef('');
    const twTimerRef = useRef(null);
    const fullRef = useRef(''); // full accumulated text independent of the typewriter

    const handleInput = () => setContent(inputRef.current.innerText.trim());
    const handleFocus = () => { };
    const handleBlur = () => setContent(inputRef.current.innerText.trim());

    const pushTypewriter = (chunk) => {
        twQueueRef.current += chunk;
        if (!twTimerRef.current) {
            twTimerRef.current = setInterval(() => {
                const q = twQueueRef.current;
                if (q.length === 0) {
                    clearInterval(twTimerRef.current);
                    twTimerRef.current = null;
                    return;
                }
                const step = Math.min(3, q.length); // 2�3 chars/tick
                const emit = q.slice(0, step);
                twQueueRef.current = q.slice(step);
                setAnswer((prev) => prev + emit);
            }, 16); // ~60fps
        }
    };

    const cleanupStream = () => {
        if (esRef.current) {
            try { esRef.current.close(); } catch { }
            esRef.current = null;
        }
        if (twTimerRef.current) {
            clearInterval(twTimerRef.current);
            twTimerRef.current = null;
        }
        twQueueRef.current = '';
    };

    useEffect(() => cleanupStream, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const message = (inputRef.current?.innerText || '').trim();
        if (!message) return;

        setIsLoading(true);
        setQuestion(message);
        setAnswer('');
        fullRef.current = '';
        cleanupStream(); // abort any previous stream

        // Immediately clear & disable the input
        if (inputRef.current) {
            inputRef.current.innerText = '';
            setContent('');
            // contentEditable is controlled by render via `!isLoading`
            // prevent focus while locked
            inputRef.current.blur();
        }

        const base = (process.env.REACT_APP_GEMINI_URL || '').replace(/\/+$/, '');
        const url = `${base}?stream=1&text=${encodeURIComponent(message)}`;

        const es = new EventSource(url);
        esRef.current = es;

        es.onmessage = (evt) => {
            try {
                const { delta } = JSON.parse(evt.data);
                if (typeof delta === 'string' && delta) {
                    fullRef.current += delta;  // keep a complete buffer
                    pushTypewriter(delta);     // animate into the UI
                }
            } catch {
                // ignore keep-alives or non-JSON lines
            }
        };

        es.addEventListener('done', async (evt) => {
            // Finalize with the full text (don�t wait for the typewriter queue)
            let finalText = fullRef.current;
            try {
                const { text } = JSON.parse(evt.data || '{}');
                if (typeof text === 'string' && text.length >= finalText.length) {
                    finalText = text;
                }
            } catch { }

            setAnswer((prev) => (finalText.length > prev.length ? finalText : prev));

            try {
                await updateGQA(message, finalText, basePath, projectName);
            } catch (e) {
                console.error('Failed to save Q&A', e);
            } finally {
                setIsLoading(false);
                if (inputRef.current) {
                    inputRef.current.innerText = '';
                }
                setContent('');
                cleanupStream();
            }
        });

        es.addEventListener('error', (err) => {
            console.error('SSE error', err);
            cleanupStream();
            setIsLoading(false);
            setAnswer((prev) => prev || 'Stream error. Please try again.');
        });
    };

    // Firestore save (kept like your original)
    const { db, doc, collection, setDoc, serverTimestamp } = require('../../Firebase');
    const updateGQA = async (message, answerText, basePath, projectName) => {
        const llmColRef = collection(db, basePath, 'llm');
        const newDocRef = doc(llmColRef);
        await setDoc(newDocRef, {
            project: projectName.split('/').join('_'),
            createdAt: serverTimestamp(),
            question: message,
            answer: answerText,
        });
    };

    return (
        <div className="qaPageR">
            <form onSubmit={handleSubmit} className="QuestionContainer">
                <div className="QuestionText">
                    <div
                        ref={inputRef}
                        contentEditable
                        onInput={handleInput}
                        className={`editable-div ${!content ? 'empty' : ''}`}
                        data-placeholder="Your question..."
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                    <div style={{ textAlign: 'right' }}>
                        <button type="submit" className="submitButton1" disabled={isLoading || !content}>
                            <FaPaperPlane size={20} />
                        </button>
                    </div>
                </div>
            </form>

            <div className="AnswerText">
                
                {question && (
                    <div className="chat-entry">
                        <FaUser size={15} className="chat-icon" />
                        <div>
                            <div className="label">Question:</div>
                            <ReactMarkdown skipHtml className="chat-text">
                                {question}
                            </ReactMarkdown>
                        </div>
                    </div>
                )}

                {(answer || isLoading) && (
                    <div className="chat-entry">
                        <FaRobot size={15} className="chat-icon" />
                        <div>
                            <div className="label">
                                Answer {isLoading && <span className="typing-dot" />}
                            </div>
                            {/* Show a loader until first chunk arrives, but DO NOT hide the answer while streaming */}
                            {isLoading && !answer && (
                                <div style={{ display: 'flex', justifyContent: 'left', padding: '12px 0px' }}>
                                    <TypingDots size={11} color="#000" />
                                </div>
                            )}

                            <ReactMarkdown
                                remarkPlugins={[remarkMath, remarkGfm]}
                                rehypePlugins={[rehypeKatex]}
                                skipHtml
                                className="chat-text"
                                // ensure line breaks/whitespace look natural while typing
                                components={{
                                    p: ({ node, ...props }) => <p style={{ whiteSpace: 'pre-wrap' }} {...props} />,
                                }}
                            >
                                {answer}
                            </ReactMarkdown>
                        </div>
                    </div>
                )}

                {answer && !isLoading && (
                    <Feedback
                        basePath={basePath}
                        projectName={projectName}
                        prediction={answer}
                        query={question}
                        feedback={feedback}
                        setFeedback={setFeedback}
                    />
                )}
            </div>
        </div>
    );
}

export default Gemini;
